import { computed, ref } from 'vue'

// 概览确认当前会员的代理身份后才读取记录；切换账户或筛选时丢弃旧响应。
export function useAgentCommissionRecords({ getToken, fetchSummary, fetchCommissions, fetchSettlements }, pageSize = 10) {
  const summary = ref(null)
  const summaryLoading = ref(false)
  const summaryError = ref('')
  const activeTab = ref('commission')
  const month = ref('')
  const records = ref([])
  const recordsLoading = ref(false)
  const recordsError = ref('')
  const total = ref(0)
  const page = ref(1)
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize)))
  let summaryVersion = 0
  let recordsVersion = 0
  let approvedToken = ''

  function clear() {
    summaryVersion += 1
    recordsVersion += 1
    approvedToken = ''
    summary.value = null
    summaryLoading.value = false
    summaryError.value = ''
    clearRecords()
  }

  function clearRecords() {
    recordsVersion += 1
    records.value = []
    recordsLoading.value = false
    recordsError.value = ''
    total.value = 0
    page.value = 1
  }

  async function loadRecords(nextPage = 1) {
    const token = getToken()
    if (!summary.value || !token || token !== approvedToken) {
      clear()
      return
    }
    const requestVersion = ++recordsVersion
    const selectedTab = activeTab.value
    const selectedMonth = month.value
    records.value = []
    total.value = 0
    recordsError.value = ''
    recordsLoading.value = true
    page.value = nextPage
    const current = () => {
      if (requestVersion !== recordsVersion) return false
      if (getToken() !== token) {
        clear()
        return false
      }
      return true
    }
    try {
      const fetchPage = selectedTab === 'commission' ? fetchCommissions : fetchSettlements
      const result = await fetchPage({
        pageNumber: nextPage,
        pageSize,
        ...(selectedMonth ? { settlementPeriod: selectedMonth } : {}),
      })
      if (!current()) return
      if (!Array.isArray(result?.data) || !Number.isSafeInteger(result.total) || result.total < 0) {
        throw new Error('佣金记录返回异常，请重新获取')
      }
      records.value = result.data
      total.value = result.total
    } catch (cause) {
      if (current()) recordsError.value = cause.message || '记录加载失败，请重试'
    } finally {
      if (current()) recordsLoading.value = false
    }
  }

  async function refresh() {
    clear()
    const token = getToken()
    if (!token) return
    const requestVersion = summaryVersion
    summaryLoading.value = true
    const current = () => {
      if (requestVersion !== summaryVersion) return false
      if (getToken() !== token) {
        clear()
        return false
      }
      return true
    }
    try {
      const result = await fetchSummary()
      if (!current()) return
      if (typeof result?.username !== 'string' || !result.username.trim()
        || typeof result.availableBalance !== 'string' || !/^[+-]?\d+(?:\.\d+)?$/.test(result.availableBalance)
        || typeof result.pendingCommission !== 'string'
        || (result.pendingCommission !== '' && !/^[+-]?\d+(?:\.\d+)?$/.test(result.pendingCommission))) {
        throw new Error('代理佣金资料返回异常，请重新获取')
      }
      summary.value = result
      approvedToken = token
      await loadRecords()
    } catch (cause) {
      if (current()) summaryError.value = cause.message || '代理概览加载失败，请重试'
    } finally {
      if (current()) summaryLoading.value = false
    }
  }

  async function selectTab(tab) {
    if (!['commission', 'settlement'].includes(tab) || activeTab.value === tab) return
    activeTab.value = tab
    await loadRecords()
  }

  async function selectMonth(value) {
    if (value && !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
      recordsError.value = '请选择有效的结算月份'
      return
    }
    month.value = value
    await loadRecords()
  }

  async function changePage(nextPage) {
    if (recordsLoading.value || nextPage < 1 || nextPage > pageCount.value) return
    await loadRecords(nextPage)
  }

  return {
    summary, summaryLoading, summaryError, activeTab, month,
    records, recordsLoading, recordsError, total, page, pageCount,
    refresh, selectTab, selectMonth, changePage, loadRecords, clear,
  }
}
